import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Play, Square, FileText, CalendarDays, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { flagFor } from "@/lib/countries";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selProject, setSelProject] = useState("");
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState([]);

  const reload = async () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const future = new Date(Date.now() + 28 * 86400000).toISOString().slice(0, 10);
    const [a, b, s] = await Promise.all([
      api.get("/dashboard/employee"),
      api.get("/projects"),
      api.get("/assignments", { params: { start_date: todayStr, end_date: future } }),
    ]);
    setD(a.data);
    setProjects(b.data);
    setSchedule(s.data);
    if (a.data.open_entry) setSelProject(a.data.open_entry.project_id);
  };

  useEffect(() => { reload(); }, []);

  const clockIn = async () => {
    if (!selProject) return toast.error("Select a project first");
    setBusy(true);
    try {
      await api.post("/time/clock-in", { project_id: selProject });
      toast.success("Clocked in");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const clockOut = async () => {
    setBusy(true);
    try {
      await api.post("/time/clock-out", {});
      toast.success("Clocked out");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!d) return <div className="label-eyebrow text-[#666]">LOADING…</div>;

  const open = d.open_entry;
  const openProject = open ? projects.find((p) => p.id === open.project_id) : null;
  const openSince = open ? new Date(open.clock_in) : null;
  const liveMs = open ? Date.now() - openSince.getTime() : 0;
  const liveH = open ? (liveMs / 3600000).toFixed(2) : "0.00";

  return (
    <div className="space-y-8" data-testid="employee-dashboard">
      <div>
        <div className="label-eyebrow">OPERATOR CONSOLE</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">
          Hello, {user.name.split(" ")[0]} {flagFor(user.country) && <span className="ml-2">{flagFor(user.country)}</span>}
        </h1>
      </div>

      {/* Clock widget */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-6 sm:p-8 relative overflow-hidden grid-bg-brand">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <div className="label-eyebrow flex items-center gap-2" style={{ color: "#8a83d4" }}>
              <span className={`h-2 w-2 rounded-full ${open ? "bg-emerald-500 pulse-dot" : "bg-[#444]"}`} />
              {open ? "ON SHIFT" : "OFF SHIFT"}
            </div>
            <div className="font-heading text-5xl sm:text-7xl font-bold tabular mt-2" data-testid="live-hours">
              {open ? `${liveH}h` : "0.00h"}
            </div>
            <div className="font-mono text-xs text-[#b9b2e8] mt-2">
              {open ? `Since ${openSince.toLocaleString()} • ${openProject?.name || "—"}` : "Select a project to begin"}
            </div>
          </div>

          <div className="space-y-3 sm:w-80">
            <Select value={selProject} onValueChange={setSelProject} disabled={!!open}>
              <SelectTrigger data-testid="project-select" className="bg-black/60 border-[#2a1f7a] rounded-none h-12 font-mono">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="font-mono">
                    {p.name} {p.aircraft_registration ? `· ${p.aircraft_registration}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {open ? (
              <Button
                onClick={clockOut}
                disabled={busy}
                data-testid="clock-out-btn"
                className="w-full h-12 rounded-none bg-red-500 hover:bg-red-400 text-black font-heading font-bold uppercase tracking-[0.15em]"
              >
                <Square className="h-4 w-4 mr-2" /> Clock Out
              </Button>
            ) : (
              <Button
                onClick={clockIn}
                disabled={busy || !selProject}
                data-testid="clock-in-btn"
                className="w-full h-12 rounded-none btn-brand text-white font-heading font-bold uppercase tracking-[0.15em] border border-[#3b2bd0]"
              >
                <Play className="h-4 w-4 mr-2" /> Clock In
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Hour summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "TODAY", value: d.hours_today },
          { label: "THIS WEEK", value: d.hours_week },
          { label: "THIS MONTH", value: d.hours_month },
        ].map((s) => (
          <Card key={s.label} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
            <div className="label-eyebrow">{s.label}</div>
            <div className="font-heading text-3xl sm:text-4xl font-bold mt-2 tabular">{s.value}h</div>
          </Card>
        ))}
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <QuickLink to="/daily-reports" icon={FileText} label="Submit Report" />
        <QuickLink to="/leaves" icon={CalendarDays} label="Request Leave" />
        <QuickLink to="/uploads" icon={Clock} label="Upload File" />
        <QuickLink to="/documents" icon={FolderKanban} label="Documents" />
      </div>

      {/* My upcoming schedule */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]" data-testid="my-schedule">
        <div className="p-5 border-b border-[#1c1c1c] flex items-center justify-between">
          <div className="label-eyebrow">MY SCHEDULE — NEXT 4 WEEKS</div>
          <span className="font-mono text-xs text-[#777]">{schedule.length} assignment{schedule.length === 1 ? "" : "s"}</span>
        </div>
        <ul className="divide-y divide-[#1c1c1c]">
          {schedule.length === 0 && <li className="p-5 text-sm font-mono text-[#666]">No upcoming assignments.</li>}
          {schedule.slice(0, 8).map((a) => {
            const statusColor = a.status === "confirmed" ? "text-emerald-400"
              : a.status === "cancelled" ? "text-red-400 line-through"
              : a.status === "completed" ? "text-[#b9b2e8]"
              : "text-[#bbb]";
            return (
              <li key={a.id} className="p-4 flex items-center justify-between text-sm gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs tabular text-[#888] w-24 shrink-0">{a.date}</span>
                    <span>{flagFor(user.country)}</span>
                    <span className="font-medium truncate">{a.project_name}</span>
                  </div>
                  <div className="font-mono text-xs text-[#888] mt-1 ml-26 truncate">
                    {a.start_time}–{a.end_time}
                    {a.location ? ` · ${a.location}` : ""}
                    {a.role ? ` · ${a.role}` : ""}
                  </div>
                </div>
                <span className={`label-eyebrow ${statusColor}`}>{a.status}</span>
              </li>
            );
          })}
        </ul>
      </Card>

      {/* My projects */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
        <div className="p-5 border-b border-[#1c1c1c] label-eyebrow">MY PROJECTS</div>
        <ul className="divide-y divide-[#1c1c1c]">
          {d.my_projects.length === 0 && <li className="p-5 text-sm font-mono text-[#666]">No projects assigned yet.</li>}
          {d.my_projects.map((p) => (
            <li key={p.id} className="p-4 flex justify-between items-center text-sm">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="font-mono text-xs text-[#666] mt-0.5">
                  {[p.client, p.aircraft_registration, p.location].filter(Boolean).join(" · ")}
                </div>
              </div>
              <span className="label-eyebrow">{p.status}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      data-testid={`quick-${label.toLowerCase().replace(/ /g, "-")}`}
      className="border border-[#1c1c1c] bg-[#0a0a0a] hover:bg-[#101010] p-4 flex flex-col items-start gap-2"
    >
      <Icon className="h-4 w-4 text-[#888]" />
      <span className="font-heading font-semibold uppercase text-sm tracking-tight">{label}</span>
    </Link>
  );
}
